/**
 * Face detection + landmark + head-pose pipeline using TFLite models.
 *
 * Ported from the Python MediaPipe reference implementation:
 *   - BlazeFace short-range detector  (128×128, anchor-based)
 *   - Face landmark detector           (192×192, 468 landmarks)
 *   - Geometry pipeline (Procrustes)    → pitch / yaw / roll
 *
 * Refs:
 *   mediapipe/modules/face_detection
 *   mediapipe/modules/face_landmark
 *   mediapipe/modules/face_geometry/libs/geometry_pipeline.cc
 */

import type { Detector } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/face-detector-provider/types';

import { CANONICAL_FACE_VERTICES } from './canonical-face-model';

// ── constants ────────────────────────────────────────────────────────────────

const SCORE_THR = 0.5;
const BOX_SCALE = 1.5;

export const PITCH_THR = 15.0;
export const YAW_THR = 11.0;
export const ROLL_THR = 15.0;

/** Below this average EAR → eyes considered closed (only evaluated when head is not rotated). */
export const EAR_THR = 0.2;

/**
 * Max relative offset of face-box center from image center.
 *
 * Vertical is intentionally looser than horizontal: the detector box center
 * sits naturally above the frame center (eyes in the upper third), and the
 * visible oval mask is far taller than wide — so a tight vertical gate rejects
 * faces the user already sees as well-centered, producing the "nudge a
 * millimetre up/down" dance.
 */
export const CENTER_OFFSET_THR_X = 0.12;
export const CENTER_OFFSET_THR_Y = 0.15;
/** @deprecated Back-compat alias; prefer the per-axis thresholds. */
export const CENTER_OFFSET_THR = CENTER_OFFSET_THR_X;

/** MediaPipe FaceMesh indices — left eye P1..P6 for EAR. */
const LEFT_EYE_IDX = [362, 385, 387, 263, 373, 380];
/** Right eye P1..P6. */
const RIGHT_EYE_IDX = [33, 160, 158, 133, 153, 144];

export const DET_INPUT_SIZE = 128;
export const LD_INPUT_SIZE = 256;

// Procrustes landmark basis weights (from geometry_pipeline_metadata_landmarks.pbtxt)
const PROCRUSTES_BASIS: Record<number, number> = {
    4: 0.070909939706326,
    6: 0.032100144773722,
    10: 0.008446550928056,
    33: 0.058724168688059,
    54: 0.007667080033571,
    67: 0.009078059345484,
    117: 0.009791937656701,
    119: 0.014565368182957,
    121: 0.018591361120343,
    127: 0.005197994410992,
    129: 0.120625205338001,
    132: 0.005560018587857,
    133: 0.05328618362546,
    136: 0.066890455782413,
    143: 0.014816547743976,
    147: 0.014262833632529,
    198: 0.025462191551924,
    205: 0.047252278774977,
    263: 0.058724168688059,
    284: 0.007667080033571,
    297: 0.009078059345484,
    346: 0.009791937656701,
    348: 0.014565368182957,
    350: 0.018591361120343,
    356: 0.005197994410992,
    358: 0.120625205338001,
    361: 0.005560018587857,
    362: 0.05328618362546,
    365: 0.066890455782413,
    372: 0.014816547743976,
    376: 0.014262833632529,
    420: 0.025462191551924,
    425: 0.047252278774977,
};

// ── types ────────────────────────────────────────────────────────────────────

export interface FaceDetection {
    box: [number, number, number, number]; // x1, y1, x2, y2 in original image coords
    keypoints: number[][]; // 6 keypoints, each [x, y]
    score: number;
}

export interface PoseAngles {
    pitch: number;
    yaw: number;
    roll: number;
}

export interface PipelineResult {
    hasFace: boolean;
    isLowQuantity?: boolean;
    faceCount: number;
    box?: [number, number, number, number];
    /** Tight bbox from face landmarks — use this for IQA instead of detector box. */
    landmarkBox?: [number, number, number, number];
    pose?: PoseAngles;
    isPoseOk?: boolean;
    isCentered?: boolean;
    eyesClosed?: boolean;
    landmarks?: number[][]; // 468 × [x, y] in original image coords
}

export type FacePipelineSource = HTMLVideoElement | HTMLCanvasElement | HTMLImageElement;

export function getFacePipelineSourceSize(source: FacePipelineSource): {
    width: number;
    height: number;
} | null {
    if (source instanceof HTMLVideoElement) {
        if (source.readyState < 4 || !source.videoWidth || !source.videoHeight) {
            return null;
        }

        return { width: source.videoWidth, height: source.videoHeight };
    }

    if (source instanceof HTMLImageElement) {
        const width = source.naturalWidth || source.width;
        const height = source.naturalHeight || source.height;

        if (!width || !height) {
            return null;
        }

        return { width, height };
    }

    if (!source.width || !source.height) {
        return null;
    }

    return { width: source.width, height: source.height };
}

// ── helpers ──────────────────────────────────────────────────────────────────

function sigmoid(x: number): number {
    const clamped = Math.max(-100, Math.min(100, x));

    return 1.0 / (1.0 + Math.exp(-clamped));
}

function dist2d(a: number[], b: number[]): number {
    const dx = a[0] - b[0];
    const dy = a[1] - b[1];

    return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Eye aspect ratio for one eye. lmPx: 468×2 pixel coords; idxs: 6 landmark indices [P1..P6].
 * EAR = (|P2-P6| + |P3-P5|) / (2 · |P1-P4|).
 */
function eyeAspectRatio(lmPx: number[][], idxs: readonly number[]): number {
    const pts = idxs.map((i) => lmPx[i]);
    const p2p6 = dist2d(pts[1], pts[5]);
    const p3p5 = dist2d(pts[2], pts[4]);
    const p1p4 = dist2d(pts[0], pts[3]);

    return (p2p6 + p3p5) / (2.0 * p1p4 + 1e-6);
}

/**
 * IQA face crop from landmarks — matches Python `analyze_selfies.py` /
 * `run_js_pipeline.js` `computeLandmarkBox`:
 * tight AABB, then extend top by 10% of face height (forehead margin).
 */
export function computeLandmarkBox(
    landmarks: number[][],
    origW: number,
    origH: number,
): [number, number, number, number] {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    landmarks.forEach(([x, y]) => {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
    });

    const x1 = Math.max(0, Math.round(minX));
    const x2 = Math.min(origW, Math.round(maxX));
    const yMin = Math.round(minY);
    const y2 = Math.min(origH, Math.round(maxY));
    const y1 = Math.max(0, yMin - Math.round((y2 - yMin) / 10));

    return [x1, y1, x2, y2];
}

/**
 * True when the face bounding-box center is within {@link CENTER_OFFSET_THR} of the
 * image center on each axis (normalized by image width / height).
 */
export function isFaceCentered(
    box: [number, number, number, number],
    imgW: number,
    imgH: number,
    offsetThrX = CENTER_OFFSET_THR_X,
    offsetThrY = CENTER_OFFSET_THR_Y,
): boolean {
    const [x1, y1, x2, y2] = box;
    const faceCx = (x1 + x2) / 2;
    const faceCy = (y1 + y2) / 2;
    const offsetX = Math.abs(faceCx - imgW / 2) / imgW;
    const offsetY = Math.abs(faceCy - imgH / 2) / imgH;

    return offsetX <= offsetThrX && offsetY <= offsetThrY;
}

/** Average EAR over both eyes (pixel-space landmarks). */
export function computeAvgEar(lmPx: number[][]): number {
    const left = eyeAspectRatio(lmPx, LEFT_EYE_IDX);
    const right = eyeAspectRatio(lmPx, RIGHT_EYE_IDX);

    return (left + right) / 2.0;
}

/**
 * Generate BlazeFace SSD anchors.
 * 128×128 → strides [8,16], per-cell counts [2,6]
 * 224×224/256×256 → strides [16,32], per-cell counts [2,6]
 */
export function makeAnchors(inputSize = 128): number[][] {
    const strides = inputSize === 128 ? [8, 16] : [16, 32];
    const perCell = [2, 6];
    const anchors: number[][] = [];

    for (let si = 0; si < strides.length; si++) {
        const stride = strides[si];
        const n = perCell[si];
        const gridSize = inputSize / stride;

        for (let y = 0; y < gridSize; y++) {
            for (let x = 0; x < gridSize; x++) {
                for (let k = 0; k < n; k++) {
                    anchors.push([(x + 0.5) / gridSize, (y + 0.5) / gridSize]);
                }
            }
        }
    }

    return anchors; // (896, 2)
}

function iouBoxes(a: number[], b: number[]): number {
    const interX1 = Math.max(a[0], b[0]);
    const interY1 = Math.max(a[1], b[1]);
    const interX2 = Math.min(a[2], b[2]);
    const interY2 = Math.min(a[3], b[3]);
    const inter = Math.max(0, interX2 - interX1) * Math.max(0, interY2 - interY1);
    const areaA = (a[2] - a[0]) * (a[3] - a[1]);
    const areaB = (b[2] - b[0]) * (b[3] - b[1]);

    return inter / (areaA + areaB - inter + 1e-6);
}

function weightedNms(
    boxes: number[][],
    scores: number[],
    keypoints: number[][][],
    threshold = 0.3,
): FaceDetection[] {
    const order = scores
        .map((s, i) => ({ s, i }))
        .sort((a, b) => b.s - a.s)
        .map((o) => o.i);

    const active = new Uint8Array(scores.length).fill(1);
    const results: FaceDetection[] = [];

    order.forEach((i) => {
        if (!active[i]) {
            return;
        }

        const candidates: number[] = [i];

        order.forEach((j) => {
            if (j !== i && active[j] && iouBoxes(boxes[i], boxes[j]) >= threshold) {
                candidates.push(j);
                active[j] = 0;
            }
        });
        active[i] = 0;

        const candScores = candidates.map((c) => scores[c]);
        const total = candScores.reduce((s, v) => s + v, 0);
        const w = candScores.map((s) => s / total);

        const blendedBox: number[] = [0, 0, 0, 0];

        for (let ci = 0; ci < candidates.length; ci++) {
            for (let k = 0; k < 4; k++) {
                blendedBox[k] += w[ci] * boxes[candidates[ci]][k];
            }
        }

        const numKps = keypoints[i].length;
        const blendedKps: number[][] = Array.from({ length: numKps }, () => [0, 0]);

        for (let ci = 0; ci < candidates.length; ci++) {
            const kp = keypoints[candidates[ci]];

            for (let k = 0; k < numKps; k++) {
                blendedKps[k][0] += w[ci] * kp[k][0];
                blendedKps[k][1] += w[ci] * kp[k][1];
            }
        }

        results.push({
            box: blendedBox as [number, number, number, number],
            keypoints: blendedKps,
            score: scores[i],
        });
    });

    return results;
}

// ── detector output processing ───────────────────────────────────────────────

/**
 * Decode BlazeFace detector output → face boxes, keypoints, scores.
 * Handles both official MediaPipe tensor names (regressors/classificators)
 * and QAI Hub split tensors (box_coords_1/2, box_scores_1/2).
 */
export function processDetectorOutput(
    outputMap: Record<string, { dataSync: () => Float32Array; shape: number[] }>,
    anchors: number[][],
    origH: number,
    origW: number,
    detSize: number,
    scoreThr = SCORE_THR,
): FaceDetection[] {
    const scale = Math.min(detSize / origH, detSize / origW);
    const xOff = (detSize - origW * scale) / 2;
    const yOff = (detSize - origH * scale) / 2;

    let coords: Float32Array;
    let rawScores: Float32Array;

    const names = Object.keys(outputMap);

    if (names.includes('regressors')) {
        coords = outputMap.regressors.dataSync();
        rawScores = outputMap.classificators.dataSync();
    } else if (names.includes('box_coords_1')) {
        const c1 = outputMap.box_coords_1.dataSync();
        const c2 = outputMap.box_coords_2.dataSync();

        coords = new Float32Array(c1.length + c2.length);
        coords.set(c1, 0);
        coords.set(c2, c1.length);

        const s1 = outputMap.box_scores_1.dataSync();
        const s2 = outputMap.box_scores_2.dataSync();

        rawScores = new Float32Array(s1.length + s2.length);
        rawScores.set(s1, 0);
        rawScores.set(s2, s1.length);
    } else {
        // Fallback: use first two outputs ordered by size (larger = coords, smaller = scores)
        const tensors = names.map((n) => ({
            name: n,
            data: outputMap[n].dataSync(),
            shape: outputMap[n].shape,
        }));

        tensors.sort((a, b) => b.data.length - a.data.length);
        coords = tensors[0].data;
        rawScores = tensors[0].data;
    }

    const numAnchors = anchors.length;
    // coords: flat array of (numAnchors * 16) values → reshape to (numAnchors, 8, 2)
    // rawScores: flat array of numAnchors values (or numAnchors * 1)

    const decoded: number[][][] = []; // numAnchors × 8 × 2

    for (let a = 0; a < numAnchors; a++) {
        const kp: number[][] = [];

        for (let k = 0; k < 8; k++) {
            const x = coords[a * 16 + k * 2];
            const y = coords[a * 16 + k * 2 + 1];

            kp.push([x, y]);
        }
        decoded.push(kp);
    }

    // Add anchor offsets (skip k=1 which is width/height)
    const ap = anchors.map(([ax, ay]) => [ax * detSize, ay * detSize]);

    for (let a = 0; a < numAnchors; a++) {
        for (let k = 0; k < 8; k++) {
            if (k !== 1) {
                decoded[a][k][0] += ap[a][0];
                decoded[a][k][1] += ap[a][1];
            }
        }
    }

    const boxes: number[][] = [];
    const keypoints: number[][][] = [];
    const scores: number[] = [];

    for (let a = 0; a < numAnchors; a++) {
        const s = sigmoid(rawScores[a]);

        if (s >= scoreThr) {
            const cx = decoded[a][0][0];
            const cy = decoded[a][0][1];
            const w = decoded[a][1][0];
            const h = decoded[a][1][1];

            boxes.push([
                (cx - w / 2 - xOff) / scale,
                (cy - h / 2 - yOff) / scale,
                (cx + w / 2 - xOff) / scale,
                (cy + h / 2 - yOff) / scale,
            ]);

            const kps: number[][] = [];

            for (let k = 2; k < 8; k++) {
                kps.push([(decoded[a][k][0] - xOff) / scale, (decoded[a][k][1] - yOff) / scale]);
            }
            keypoints.push(kps);
            scores.push(s);
        }
    }

    if (boxes.length === 0) return [];

    return weightedNms(boxes, scores, keypoints);
}

// ── affine transform helpers ─────────────────────────────────────────────────

/**
 * Solve 2×3 affine matrix M that maps 3 src points → 3 dst points.
 * Equivalent to cv2.getAffineTransform.
 */
export function getAffineTransform(src: number[][], dst: number[][]): number[][] {
    // Solve: dst = M * [src; 1]
    // M is 2×3: [[a, b, e], [c, d, f]]
    const [sx0, sy0] = src[0];
    const [sx1, sy1] = src[1];
    const [sx2, sy2] = src[2];

    const det = sx0 * (sy1 - sy2) - sy0 * (sx1 - sx2) + (sx1 * sy2 - sx2 * sy1);

    if (Math.abs(det) < 1e-12)
        return [
            [1, 0, 0],
            [0, 1, 0],
        ];
    const invDet = 1 / det;

    // Inverse of 3×3 source matrix [[sx0,sx1,sx2],[sy0,sy1,sy2],[1,1,1]]
    const i00 = (sy1 - sy2) * invDet;
    const i01 = (sx2 - sx1) * invDet;
    const i02 = (sx1 * sy2 - sx2 * sy1) * invDet;
    const i10 = (sy2 - sy0) * invDet;
    const i11 = (sx0 - sx2) * invDet;
    const i12 = (sx2 * sy0 - sx0 * sy2) * invDet;
    const i20 = (sy0 - sy1) * invDet;
    const i21 = (sx1 - sx0) * invDet;
    const i22 = (sx0 * sy1 - sx1 * sy0) * invDet;

    const [dx0, dy0] = dst[0];
    const [dx1, dy1] = dst[1];
    const [dx2, dy2] = dst[2];

    return [
        [
            dx0 * i00 + dx1 * i10 + dx2 * i20,
            dx0 * i01 + dx1 * i11 + dx2 * i21,
            dx0 * i02 + dx1 * i12 + dx2 * i22,
        ],
        [
            dy0 * i00 + dy1 * i10 + dy2 * i20,
            dy0 * i01 + dy1 * i11 + dy2 * i21,
            dy0 * i02 + dy1 * i12 + dy2 * i22,
        ],
    ];
}

/**
 * Invert a 2×3 affine matrix.
 * Equivalent to cv2.invertAffineTransform.
 */
export function invertAffineTransform(M: number[][]): number[][] {
    const [a, b, e] = M[0];
    const [c, d, f] = M[1];
    const det = a * d - b * c;

    if (Math.abs(det) < 1e-12)
        return [
            [1, 0, 0],
            [0, 1, 0],
        ];
    const id = 1 / det;

    return [
        [d * id, -b * id, (b * f - d * e) * id],
        [-c * id, a * id, (c * e - a * f) * id],
    ];
}

// ── ROI crop ─────────────────────────────────────────────────────────────────

/**
 * Compute oriented square ROI from detection box + keypoints, warp to (ldH×ldW).
 * Returns the patch canvas and inverse affine transform.
 */
export function cropFaceROI(
    source: FacePipelineSource | OffscreenCanvas,
    box: [number, number, number, number],
    kps: number[][],
    ldH: number,
    ldW: number,
    boxScale = BOX_SCALE,
    srcW?: number,
    srcH?: number,
): { canvas: HTMLCanvasElement | OffscreenCanvas; mInv: number[][]; roiSize: number } {
    const [x1, y1, x2, y2] = box;
    const cx = (x1 + x2) / 2;
    const cy = (y1 + y2) / 2;
    const size = Math.max(x2 - x1, y2 - y1) * boxScale;

    const angle = Math.atan2(kps[1][1] - kps[0][1], kps[1][0] - kps[0][0]);
    const ca = Math.cos(angle);
    const sa = Math.sin(angle);
    const half = size / 2;

    // right and down unit vectors (rotated)
    const rx = ca;
    const ry = sa;
    const dx = -sa;
    const dy = ca;

    const tl = [cx - half * rx - half * dx, cy - half * ry - half * dy];
    const bl = [cx - half * rx + half * dx, cy - half * ry + half * dy];
    const tr = [cx + half * rx - half * dx, cy + half * ry - half * dy];

    const srcPts = [tl, bl, tr];
    const dstPts = [
        [0, 0],
        [0, ldH],
        [ldW, 0],
    ];

    const M = getAffineTransform(srcPts, dstPts);
    const mInv = invertAffineTransform(M);

    const roiCanvas =
        typeof OffscreenCanvas === 'undefined'
            ? document.createElement('canvas')
            : new OffscreenCanvas(ldW, ldH);

    if (roiCanvas instanceof HTMLCanvasElement) {
        roiCanvas.width = ldW;
        roiCanvas.height = ldH;
    }

    const ctx = roiCanvas.getContext('2d') as
        | CanvasRenderingContext2D
        | OffscreenCanvasRenderingContext2D;

    // Simulate BORDER_REPLICATE (MediaPipe default for landmark crop) by pre-padding
    // the source canvas with replicated edge pixels before applying the affine transform.
    let drawSource: CanvasImageSource = source as CanvasImageSource;
    let drawM = M;

    if (srcW !== undefined && srcH !== undefined) {
        const pad = Math.ceil(size / 2) + 4;
        const paddedW = srcW + 2 * pad;
        const paddedH = srcH + 2 * pad;

        const paddedCanvas =
            typeof OffscreenCanvas === 'undefined'
                ? document.createElement('canvas')
                : new OffscreenCanvas(paddedW, paddedH);

        if (paddedCanvas instanceof HTMLCanvasElement) {
            paddedCanvas.width = paddedW;
            paddedCanvas.height = paddedH;
        }

        const pctx = paddedCanvas.getContext('2d') as
            | CanvasRenderingContext2D
            | OffscreenCanvasRenderingContext2D;

        pctx.drawImage(drawSource, pad, pad, srcW, srcH);
        // Replicate edges (9-grid: 4 edges + 4 corners)
        pctx.drawImage(drawSource, 0, 0, srcW, 1, pad, 0, srcW, pad);
        pctx.drawImage(drawSource, 0, srcH - 1, srcW, 1, pad, pad + srcH, srcW, pad);
        pctx.drawImage(drawSource, 0, 0, 1, srcH, 0, pad, pad, srcH);
        pctx.drawImage(drawSource, srcW - 1, 0, 1, srcH, pad + srcW, pad, pad, srcH);
        pctx.drawImage(drawSource, 0, 0, 1, 1, 0, 0, pad, pad);
        pctx.drawImage(drawSource, srcW - 1, 0, 1, 1, pad + srcW, 0, pad, pad);
        pctx.drawImage(drawSource, 0, srcH - 1, 1, 1, 0, pad + srcH, pad, pad);
        pctx.drawImage(drawSource, srcW - 1, srcH - 1, 1, 1, pad + srcW, pad + srcH, pad, pad);

        drawSource = paddedCanvas as CanvasImageSource;
        // Adjust translation: padded coords = original + pad, so shift M's translation
        drawM = [
            [M[0][0], M[0][1], M[0][2] - M[0][0] * pad - M[0][1] * pad],
            [M[1][0], M[1][1], M[1][2] - M[1][0] * pad - M[1][1] * pad],
        ];
    }

    ctx.setTransform(drawM[0][0], drawM[1][0], drawM[0][1], drawM[1][1], drawM[0][2], drawM[1][2]);
    ctx.drawImage(drawSource, 0, 0);
    ctx.resetTransform();

    return { canvas: roiCanvas, mInv, roiSize: size };
}

// ── landmark output processing ───────────────────────────────────────────────

/**
 * Decode face landmark model output → 468 landmarks (x,y,z) normalized [0,1] + confidence score.
 * Handles both official MediaPipe and QAI Hub output formats.
 */
export function processLandmarkOutput(
    outputMap: Record<string, { dataSync: () => Float32Array; shape: number[] }>,
    ldW: number,
    ldH: number,
): { landmarks: number[][] | null; score: number } {
    const names = Object.keys(outputMap);

    let rawData: Float32Array | null = null;
    let flagData: Float32Array | null = null;

    if (names.includes('landmarks')) {
        rawData = outputMap.landmarks.dataSync();
        flagData = (outputMap.scores ?? outputMap.face_flag)?.dataSync() ?? null;
    } else {
        // Fallback: find the tensor with 468*3=1404 elements (landmarks) and the smallest (score)
        names.forEach((name) => {
            const data = outputMap[name].dataSync();

            if (data.length % 3 === 0 && data.length >= 1404 && !rawData) {
                rawData = data;
            } else if (data.length === 1 && !flagData) {
                flagData = data;
            }
        });
    }

    if (!rawData) return { landmarks: null, score: 0 };

    // Reshape to (N, 3), truncate to 468 (geometry pipeline uses only first 468,
    // not iris landmarks at indices 468-477 from 478-landmark models).
    const numLandmarks = Math.min(rawData.length / 3, 468);
    const lm: number[][] = [];

    for (let i = 0; i < numLandmarks; i++) {
        lm.push([rawData[i * 3], rawData[i * 3 + 1], rawData[i * 3 + 2]]);
    }

    // If values are in pixel space (> 1.5), normalize to [0,1]
    let maxXY = 0;

    lm.forEach((pt) => {
        maxXY = Math.max(maxXY, Math.abs(pt[0]), Math.abs(pt[1]));
    });
    if (maxXY > 1.5) {
        lm.forEach((pt) => {
            pt[0] /= ldW;
            pt[1] /= ldH;
            pt[2] /= ldW; // z normalized by width per MediaPipe convention
        });
    }

    const score = flagData ? sigmoid(flagData[0]) : 1.0;

    return { landmarks: lm, score };
}

// ── 3×3 linear algebra helpers ───────────────────────────────────────────────

function mat3Mul(A: number[][], B: number[][]): number[][] {
    const C: number[][] = [
        [0, 0, 0],
        [0, 0, 0],
        [0, 0, 0],
    ];

    for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
            for (let k = 0; k < 3; k++) {
                C[i][j] += A[i][k] * B[k][j];
            }
        }
    }

    return C;
}

function mat3Det(A: number[][]): number {
    return (
        A[0][0] * (A[1][1] * A[2][2] - A[1][2] * A[2][1]) -
        A[0][1] * (A[1][0] * A[2][2] - A[1][2] * A[2][0]) +
        A[0][2] * (A[1][0] * A[2][1] - A[1][1] * A[2][0])
    );
}

function mat3Transpose(A: number[][]): number[][] {
    return [
        [A[0][0], A[1][0], A[2][0]],
        [A[0][1], A[1][1], A[2][1]],
        [A[0][2], A[1][2], A[2][2]],
    ];
}

function vec3Norm(v: number[]): number {
    return Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
}

function vec3Dot(a: number[], b: number[]): number {
    return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

/**
 * Jacobi eigenvalue decomposition of a 3×3 symmetric matrix.
 * Returns eigenvalues and eigenvectors (columns of the returned matrix).
 */
function jacobiEigen3x3(A: number[][]): { values: number[]; vectors: number[][] } {
    const a = A.map((row) => [...row]);
    const v: number[][] = [
        [1, 0, 0],
        [0, 1, 0],
        [0, 0, 1],
    ];

    for (let iter = 0; iter < 100; iter++) {
        let maxOff = 0;
        let p = 0;
        let q = 1;

        for (let i = 0; i < 3; i++) {
            for (let j = i + 1; j < 3; j++) {
                if (Math.abs(a[i][j]) > maxOff) {
                    maxOff = Math.abs(a[i][j]);
                    p = i;
                    q = j;
                }
            }
        }
        if (maxOff < 1e-15) break;

        const diff = a[p][p] - a[q][q];
        let tanTheta: number;

        if (Math.abs(diff) < 1e-15) {
            tanTheta = 1;
        } else {
            const tau = diff / (2 * a[p][q]);

            tanTheta = Math.sign(tau) / (Math.abs(tau) + Math.sqrt(1 + tau * tau));
        }
        const c = 1 / Math.sqrt(1 + tanTheta * tanTheta);
        const s = tanTheta * c;

        const apq = a[p][q];

        a[p][p] += tanTheta * apq;
        a[q][q] -= tanTheta * apq;
        a[p][q] = 0;
        a[q][p] = 0;

        for (let r = 0; r < 3; r++) {
            if (r !== p && r !== q) {
                const arp = a[r][p];
                const arq = a[r][q];
                const rp = c * arp + s * arq;
                const rq = -s * arp + c * arq;

                a[r][p] = rp;
                a[p][r] = rp;
                a[r][q] = rq;
                a[q][r] = rq;
            }
        }

        for (let r = 0; r < 3; r++) {
            const vrp = v[r][p];
            const vrq = v[r][q];

            v[r][p] = c * vrp + s * vrq;
            v[r][q] = -s * vrp + c * vrq;
        }
    }

    return { values: [a[0][0], a[1][1], a[2][2]], vectors: v };
}

/**
 * Pure-JS SVD of a 3×3 matrix via eigendecomposition of H^T·H.
 * Returns { U, sv, V } where H ≈ U × diag(sv) × V^T.
 */
function svd3x3(H: number[][]): { U: number[][]; sv: number[]; V: number[][] } {
    const HtH = mat3Mul(mat3Transpose(H), H);
    const { values, vectors } = jacobiEigen3x3(HtH);

    // Sort by decreasing eigenvalue
    const idx = [0, 1, 2].sort((a, b) => values[b] - values[a]);
    const sortedSv = idx.map((i) => Math.sqrt(Math.max(0, values[i])));
    const V: number[][] = [
        [vectors[0][idx[0]], vectors[0][idx[1]], vectors[0][idx[2]]],
        [vectors[1][idx[0]], vectors[1][idx[1]], vectors[1][idx[2]]],
        [vectors[2][idx[0]], vectors[2][idx[1]], vectors[2][idx[2]]],
    ];

    // U = H · V · diag(1/σ)
    const HV = mat3Mul(H, V);
    const U: number[][] = [
        [0, 0, 0],
        [0, 0, 0],
        [0, 0, 0],
    ];

    for (let j = 0; j < 3; j++) {
        if (sortedSv[j] > 1e-10) {
            const invS = 1 / sortedSv[j];

            for (let i = 0; i < 3; i++) U[i][j] = HV[i][j] * invS;
        }
    }

    // Fill any zero-singular-value column via cross product of the other two
    for (let j = 0; j < 3; j++) {
        if (sortedSv[j] <= 1e-10) {
            const a = (j + 1) % 3;
            const b = (j + 2) % 3;

            U[0][j] = U[1][a] * U[2][b] - U[2][a] * U[1][b];
            U[1][j] = U[2][a] * U[0][b] - U[0][a] * U[2][b];
            U[2][j] = U[0][a] * U[1][b] - U[1][a] * U[0][b];
        }
    }

    return { U, sv: sortedSv, V };
}

// ── Procrustes alignment ─────────────────────────────────────────────────────

function buildProcrustesWeights(n = 468): number[] {
    const w = new Array<number>(n).fill(0);

    Object.entries(PROCRUSTES_BASIS).forEach(([idx, val]) => {
        w[Number(idx)] = val;
    });

    return w;
}

/**
 * Closed-form weighted orthogonal Procrustes.
 * src, tgt: 3×N matrices (rows = x,y,z; columns = landmarks).
 * w: N weights.
 * Returns 4×4 [s·R | t; 0 0 0 1].
 */
function procrustes(src: number[][], tgt: number[][], w: number[]): number[][] {
    const N = w.length;
    let W = 0;

    for (let i = 0; i < N; i++) W += w[i];

    // Weighted centroids
    const ms = [0, 0, 0];
    const mt = [0, 0, 0];

    for (let i = 0; i < N; i++) {
        for (let d = 0; d < 3; d++) {
            ms[d] += src[d][i] * w[i];
            mt[d] += tgt[d][i] * w[i];
        }
    }
    for (let d = 0; d < 3; d++) {
        ms[d] /= W;
        mt[d] /= W;
    }

    // Centered data
    const sc: number[][] = [new Array(N), new Array(N), new Array(N)];
    const tc: number[][] = [new Array(N), new Array(N), new Array(N)];

    for (let i = 0; i < N; i++) {
        for (let d = 0; d < 3; d++) {
            sc[d][i] = src[d][i] - ms[d];
            tc[d][i] = tgt[d][i] - mt[d];
        }
    }

    // H = sc @ (tc * w).T  →  3×3
    const H: number[][] = [
        [0, 0, 0],
        [0, 0, 0],
        [0, 0, 0],
    ];

    for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
            let s = 0;

            for (let i = 0; i < N; i++) {
                s += sc[r][i] * tc[c][i] * w[i];
            }
            H[r][c] = s;
        }
    }

    const { U, sv, V } = svd3x3(H);
    // V here is the right singular vectors (columns of V), same as Vt.T in numpy
    const VUt = mat3Mul(V, mat3Transpose(U));
    const d = mat3Det(VUt);
    const D: number[][] = [
        [1, 0, 0],
        [0, 1, 0],
        [0, 0, d],
    ];
    const R = mat3Mul(mat3Mul(V, D), mat3Transpose(U));

    const sDot = sv[0] + sv[1] + d * sv[2];
    let scSqW = 0;

    for (let r = 0; r < 3; r++) {
        for (let i = 0; i < N; i++) {
            scSqW += sc[r][i] * sc[r][i] * w[i];
        }
    }
    const scaleFactor = sDot / scSqW;

    // t = mt - s * R @ ms
    const Rms = [
        R[0][0] * ms[0] + R[0][1] * ms[1] + R[0][2] * ms[2],
        R[1][0] * ms[0] + R[1][1] * ms[1] + R[1][2] * ms[2],
        R[2][0] * ms[0] + R[2][1] * ms[1] + R[2][2] * ms[2],
    ];
    const t = [
        mt[0] - scaleFactor * Rms[0],
        mt[1] - scaleFactor * Rms[1],
        mt[2] - scaleFactor * Rms[2],
    ];

    const m: number[][] = [
        [scaleFactor * R[0][0], scaleFactor * R[0][1], scaleFactor * R[0][2], t[0]],
        [scaleFactor * R[1][0], scaleFactor * R[1][1], scaleFactor * R[1][2], t[1]],
        [scaleFactor * R[2][0], scaleFactor * R[2][1], scaleFactor * R[2][2], t[2]],
        [0, 0, 0, 1],
    ];

    return m;
}

// ── head-pose estimation ─────────────────────────────────────────────────────

/**
 * Two-pass Procrustes head-pose estimation (MediaPipe geometry pipeline).
 * lmOrig: 468×2 landmarks in original image space.
 * lmZ: 468 z-values (normalized [0,1]).
 * Returns 4×4 pose matrix or null.
 */
export function estimatePose(
    lmOrig: number[][],
    lmZ: number[],
    canonical: number[][],
    weights: number[],
    imgH: number,
    imgW: number,
    fov = 63.0,
    near = 1.0,
): number[][] | null {
    const N = lmOrig.length;

    // screen: Nx3, col-stacked [x/w, y/h, z]
    const screen: number[][] = [new Array(N), new Array(N), new Array(N)];

    for (let i = 0; i < N; i++) {
        screen[0][i] = lmOrig[i][0] / imgW;
        screen[1][i] = lmOrig[i][1] / imgH;
        screen[2][i] = lmZ[i];
    }

    const hN = 2 * near * Math.tan((0.5 * fov * Math.PI) / 180);
    const wN = (imgW * hN) / imgH;
    const l = -0.5 * wN;
    const r = 0.5 * wN;
    const b = -0.5 * hN;
    const t = 0.5 * hN;

    // Transform landmarks to near-plane coords
    const lm: number[][] = [new Array(N), new Array(N), new Array(N)];

    for (let i = 0; i < N; i++) {
        lm[0][i] = screen[0][i] * (r - l) + l;
        lm[1][i] = (1 - screen[1][i]) * (t - b) + b;
        lm[2][i] = screen[2][i] * (r - l);
    }

    let doff = 0;

    for (let i = 0; i < N; i++) doff += lm[2][i];
    doff /= N;

    // Canonical model: transpose to 3×N
    const src: number[][] = [new Array(N), new Array(N), new Array(N)];

    for (let i = 0; i < N; i++) {
        const [x, y, z] = canonical[i];

        src[0][i] = x;
        src[1][i] = y;
        src[2][i] = z;
    }

    // Pass 1: estimate initial scale
    const i1: number[][] = [new Array(N), new Array(N), new Array(N)];

    for (let i = 0; i < N; i++) {
        i1[0][i] = lm[0][i];
        i1[1][i] = lm[1][i];
        i1[2][i] = -lm[2][i];
    }
    const p1 = procrustes(src, i1, weights);
    const s1 = vec3Norm([p1[0][0], p1[1][0], p1[2][0]]);

    // Pass 2: refine with depth correction
    const i2: number[][] = [new Array(N), new Array(N), new Array(N)];

    for (let i = 0; i < N; i++) {
        const z2 = (lm[2][i] - doff + near) / s1;

        i2[0][i] = (lm[0][i] * z2) / near;
        i2[1][i] = (lm[1][i] * z2) / near;
        i2[2][i] = -z2;
    }
    const p2 = procrustes(src, i2, weights);
    const s2 = vec3Norm([p2[0][0], p2[1][0], p2[2][0]]);

    // Final pass
    const ts = s1 * s2;
    const m: number[][] = [new Array(N), new Array(N), new Array(N)];

    for (let i = 0; i < N; i++) {
        const zf = (lm[2][i] - doff + near) / ts;

        m[0][i] = (lm[0][i] * zf) / near;
        m[1][i] = (lm[1][i] * zf) / near;
        m[2][i] = -zf;
    }

    try {
        return procrustes(src, m, weights);
    } catch {
        return null;
    }
}

// ── matrix decomposition → Euler angles ──────────────────────────────────────

/**
 * Extract rotation matrix from 4×4 affine via polar decomposition.
 */
function decompose44(A44: number[][]): number[][] {
    // Upper-left 3×3 (RZS), transposed columns → M0, M1, M2
    const M0 = [A44[0][0], A44[1][0], A44[2][0]];
    const M1 = [A44[0][1], A44[1][1], A44[2][1]];
    const M2 = [A44[0][2], A44[1][2], A44[2][2]];

    let sx = vec3Norm(M0);

    M0[0] /= sx;
    M0[1] /= sx;
    M0[2] /= sx;

    const sxSxy = vec3Dot(M0, M1);

    M1[0] -= sxSxy * M0[0];
    M1[1] -= sxSxy * M0[1];
    M1[2] -= sxSxy * M0[2];

    const sy = vec3Norm(M1);

    M1[0] /= sy;
    M1[1] /= sy;
    M1[2] /= sy;

    const sxSxz = vec3Dot(M0, M2);
    const sySyz = vec3Dot(M1, M2);

    M2[0] -= sxSxz * M0[0] + sySyz * M1[0];
    M2[1] -= sxSxz * M0[1] + sySyz * M1[1];
    M2[2] -= sxSxz * M0[2] + sySyz * M1[2];

    const sz = vec3Norm(M2);

    M2[0] /= sz;
    M2[1] /= sz;
    M2[2] /= sz;

    const R = [
        [M0[0], M1[0], M2[0]],
        [M0[1], M1[1], M2[1]],
        [M0[2], M1[2], M2[2]],
    ];

    if (mat3Det(R) < 0) {
        sx *= -1;
        R[0][0] *= -1;
        R[1][0] *= -1;
        R[2][0] *= -1;
    }

    return R;
}

/**
 * Extract (pitch, yaw, roll) in degrees from a 4×4 pose matrix.
 */
export function anglesFromMatrix(mat44: number[][]): PoseAngles {
    const R = decompose44(mat44);
    const sy = Math.sqrt(R[0][0] ** 2 + R[1][0] ** 2);
    const singular = sy < 1e-6;

    let pitch: number;
    let yaw: number;
    let roll: number;

    if (singular) {
        pitch = Math.atan2(-R[1][2], R[1][1]);
        yaw = Math.atan2(-R[2][0], sy);
        roll = 0;
    } else {
        pitch = Math.atan2(R[2][1], R[2][2]);
        yaw = Math.atan2(-R[2][0], sy);
        roll = Math.atan2(R[1][0], R[0][0]);
    }

    return {
        pitch: pitch * (180 / Math.PI),
        yaw: yaw * (180 / Math.PI),
        roll: roll * (180 / Math.PI),
    };
}

// ── precomputed state ────────────────────────────────────────────────────────

let anchorsCache: number[][] | null = null;
let procrustesWeightsCache: number[] | null = null;

function getAnchors(): number[][] {
    if (!anchorsCache) anchorsCache = makeAnchors(DET_INPUT_SIZE);

    return anchorsCache;
}

function getProcrustesWeights(): number[] {
    if (!procrustesWeightsCache) procrustesWeightsCache = buildProcrustesWeights();

    return procrustesWeightsCache;
}

// ── temporal smoothing ───────────────────────────────────────────────────────

/** EMA weight of the newest pose sample; lower = smoother but laggier. */
const POSE_SMOOTH_ALPHA = 0.5;

let poseSmoothCache: PoseAngles | null = null;

/**
 * Reset per-session smoothing state. Call when a new liveness scan begins so a
 * previous run's last pose doesn't bleed into the first frames of the new one.
 */
export function resetFacePipelineState(): void {
    poseSmoothCache = null;
}

/**
 * Exponential moving average of the head-pose angles. The Procrustes pose
 * estimate is noisy frame-to-frame; smoothing keeps the pass/fail boolean from
 * flickering when the user is holding still near a threshold.
 */
function smoothPose(p: PoseAngles): PoseAngles {
    if (!poseSmoothCache) {
        poseSmoothCache = { ...p };

        return poseSmoothCache;
    }

    poseSmoothCache = {
        pitch: POSE_SMOOTH_ALPHA * p.pitch + (1 - POSE_SMOOTH_ALPHA) * poseSmoothCache.pitch,
        yaw: POSE_SMOOTH_ALPHA * p.yaw + (1 - POSE_SMOOTH_ALPHA) * poseSmoothCache.yaw,
        roll: POSE_SMOOTH_ALPHA * p.roll + (1 - POSE_SMOOTH_ALPHA) * poseSmoothCache.roll,
    };

    return poseSmoothCache;
}

type ModelOutputTensor = {
    dataSync: () => Float32Array;
    shape: number[];
    dispose?: () => void;
};

type NormalizedOutputMap = Record<
    string,
    { dataSync: () => Float32Array; shape: number[]; dispose: () => void }
>;

function isModelOutputTensor(v: unknown): v is ModelOutputTensor {
    return (
        typeof v === 'object' &&
        v !== null &&
        typeof (v as ModelOutputTensor).dataSync === 'function' &&
        Array.isArray((v as ModelOutputTensor).shape)
    );
}

/**
 * Normalize TF.js predict() output to a keyed map.
 * Accepts single tensor, Tensor[] array, NamedTensorMap, or null-ish.
 */
function normalizeOutput(output: unknown): NormalizedOutputMap {
    const wrap = (t: ModelOutputTensor): NormalizedOutputMap[string] => ({
        dataSync: () => t.dataSync(),
        shape: [...t.shape],
        dispose:
            typeof t.dispose === 'function'
                ? () => {
                      t.dispose?.();
                  }
                : () => undefined,
    });

    if (isModelOutputTensor(output)) {
        return { output: wrap(output) };
    }

    if (Array.isArray(output)) {
        const map: NormalizedOutputMap = {};

        output.forEach((t, i) => {
            if (isModelOutputTensor(t)) {
                map[String(i)] = wrap(t);
            }
        });

        return map;
    }

    if (typeof output === 'object' && output !== null) {
        const map: NormalizedOutputMap = {};

        Object.keys(output).forEach((k) => {
            const t = (output as Record<string, unknown>)[k];

            if (isModelOutputTensor(t)) {
                map[k] = wrap(t);
            }
        });

        return map;
    }

    return {};
}

// ── main pipeline function ───────────────────────────────────────────────────

/**
 * Run the full face-detection + landmark + pose pipeline on a video or still image.
 *
 * @param detectorModel  TFLite model (face detector)
 * @param landmarkModel  TFLite model (face landmark)
 * @param source         Live video or captured still (canvas / image)
 * @returns Pipeline result with face detection, pose angles, and alignment check.
 */
export function runFacePipeline(
    detectorModel: Detector,
    landmarkModel: Detector,
    source: FacePipelineSource,
): PipelineResult | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { tf } = window as any;
    const size = getFacePipelineSourceSize(source);

    if (!tf || !source || !size) return null;

    const { height: origH, width: origW } = size;

    const detSize = DET_INPUT_SIZE;
    const ldSize = LD_INPUT_SIZE;
    const anchors = getAnchors();

    // ── Step 1: prepare detector input (center letterbox + normalize to [-1,1]) ──
    // Use tf.image.resizeBilinear (halfPixelCenters=false) to match cv2.warpAffine(INTER_LINEAR)
    // exactly. canvas.drawImage uses area-averaging at large downscale factors, which diverges.
    const scale = Math.min(detSize / origH, detSize / origW);
    const newH = Math.round(origH * scale);
    const newW = Math.round(origW * scale);
    const padTop = Math.round((detSize - newH) / 2);
    const padLeft = Math.round((detSize - newW) / 2);
    const padBottom = detSize - newH - padTop;
    const padRight = detSize - newW - padLeft;

    const detInput = tf.tidy(() => {
        const raw = tf.browser.fromPixels(source as HTMLCanvasElement);
        const float32 = tf.cast(raw, 'float32');
        const resized = tf.image.resizeBilinear(float32, [newH, newW], false, false);
        const padded = tf.pad(resized, [
            [padTop, padBottom],
            [padLeft, padRight],
            [0, 0],
        ]);

        return tf.expandDims(tf.sub(tf.div(padded, 127.5), 1.0));
    });

    // ── Step 2: run detector ──
    const detOutput = detectorModel.predict(detInput);

    detInput.dispose();

    const detOutputMap = normalizeOutput(detOutput);

    const faces = processDetectorOutput(detOutputMap, anchors, origH, origW, detSize);

    // Dispose detector output tensors
    Object.values(detOutputMap).forEach((t) => {
        if (t && typeof t.dispose === 'function') t.dispose();
    });

    if (faces.length === 0) {
        return { hasFace: false, faceCount: 0 };
    }

    // Use the highest-scoring face
    const face = faces[0];

    // ── Step 3: compute face ROI affine transform ──
    // mInv = inverse affine (dst→src) used by tf.image.transform below and by landmark back-projection.
    // roiSize needed for z-scaling (MediaPipe LandmarkProjectionCalculator).
    const [bx1, by1, bx2, by2] = face.box;
    const roiCx = (bx1 + bx2) / 2;
    const roiCy = (by1 + by2) / 2;
    const roiSize = Math.max(bx2 - bx1, by2 - by1) * BOX_SCALE;
    const roiAngle = Math.atan2(
        face.keypoints[1][1] - face.keypoints[0][1],
        face.keypoints[1][0] - face.keypoints[0][0],
    );
    const rca = Math.cos(roiAngle);
    const rsa = Math.sin(roiAngle);
    const half = roiSize / 2;
    const tl = [roiCx - half * rca + half * rsa, roiCy - half * rsa - half * rca];
    const bl = [roiCx - half * rca - half * rsa, roiCy - half * rsa + half * rca];
    const tr = [roiCx + half * rca + half * rsa, roiCy + half * rsa - half * rca];
    const roiM = getAffineTransform(
        [tl, bl, tr],
        [
            [0, 0],
            [0, ldSize],
            [ldSize, 0],
        ],
    );
    const mInv = invertAffineTransform(roiM);

    // ── Step 4: run landmark model ──
    const ldInput = tf.tidy(() => {
        const raw = tf.browser.fromPixels(source as HTMLCanvasElement);
        const float32 = tf.cast(raw, 'float32');
        const xformed = tf.image.transform(
            tf.expandDims(float32, 0),
            tf.tensor2d(
                [[mInv[0][0], mInv[0][1], mInv[0][2], mInv[1][0], mInv[1][1], mInv[1][2], 0, 0]],
                [1, 8],
            ),
            'bilinear',
            'nearest',
            0,
            [ldSize, ldSize],
        );

        return tf.expandDims(tf.div(tf.squeeze(xformed, [0]), 255.0), 0);
    });

    const ldOutput = landmarkModel.predict(ldInput);

    ldInput.dispose();

    const ldOutputMap = normalizeOutput(ldOutput);

    const { landmarks: lmXYZ, score: ldScore } = processLandmarkOutput(ldOutputMap, ldSize, ldSize);

    Object.values(ldOutputMap).forEach((t) => {
        if (t && typeof t.dispose === 'function') t.dispose();
    });

    // Box-center fallback (used only when landmarks are unavailable below).
    let isCentered = isFaceCentered(face.box, origW, origH);

    if (!lmXYZ || ldScore < 0.55) {
        return {
            hasFace: true,
            isLowQuantity: true,
            faceCount: faces.length,
            box: face.box,
            isCentered,
        };
    }

    // ── Step 5: map landmarks back to original image space ──
    const lmOrig: number[][] = [];

    for (let i = 0; i < lmXYZ.length; i++) {
        const px = lmXYZ[i][0] * ldSize;
        const py = lmXYZ[i][1] * ldSize;
        const ox = mInv[0][0] * px + mInv[0][1] * py + mInv[0][2];
        const oy = mInv[1][0] * px + mInv[1][1] * py + mInv[1][2];

        lmOrig.push([ox, oy]);
    }

    // IQA crop box — same as Python analyze_selfies / run_js_pipeline computeLandmarkBox.
    const landmarkBox = computeLandmarkBox(lmOrig, origW, origH);

    // Re-center on the landmark centroid instead of the detector box center.
    // For a close face the box top (forehead) reaches the frame edge and drags
    // the box center upward, failing the vertical offset check even though the
    // face is fully visible and well-centered. The centroid stays put no matter
    // how much the face fills — or slightly overflows — the oval.
    let cxSum = 0;
    let cySum = 0;

    for (let i = 0; i < lmOrig.length; i++) {
        cxSum += lmOrig[i][0];
        cySum += lmOrig[i][1];
    }
    const faceCx = cxSum / lmOrig.length;
    const faceCy = cySum / lmOrig.length;

    isCentered =
        Math.abs(faceCx - origW / 2) / origW <= CENTER_OFFSET_THR_X &&
        Math.abs(faceCy - origH / 2) / origH <= CENTER_OFFSET_THR_Y;

    // Scale z from patch-normalized to image-normalized space (MediaPipe LandmarkProjectionCalculator).
    const roiWidthNorm = roiSize / origW;
    const lmZ = lmXYZ.map((pt) => pt[2] * roiWidthNorm);

    // ── Step 6: estimate head pose ──
    const mat44 = estimatePose(
        lmOrig,
        lmZ,
        CANONICAL_FACE_VERTICES as unknown as number[][],
        getProcrustesWeights(),
        origH,
        origW,
    );

    if (!mat44) {
        return {
            hasFace: true,
            faceCount: faces.length,
            box: face.box,
            landmarkBox,
            landmarks: lmOrig,
            isCentered,
        };
    }

    const pose = smoothPose(anglesFromMatrix(mat44));
    const headRotated =
        Math.abs(pose.pitch) >= PITCH_THR ||
        Math.abs(pose.yaw) >= YAW_THR ||
        Math.abs(pose.roll) >= ROLL_THR;
    const isPoseOk = !headRotated;

    const ear = computeAvgEar(lmOrig);
    // EAR only meaningful when head is not already rotated (pitch/roll compress EAR for open eyes).
    const eyesClosed = !headRotated && ear < EAR_THR;

    return {
        hasFace: true,
        faceCount: faces.length,
        box: face.box,
        landmarkBox,
        pose,
        isPoseOk,
        isCentered,
        eyesClosed,
        landmarks: lmOrig,
    };
}
